import Button from '@atlaskit/button/new';
import DynamicTable from '@atlaskit/dynamic-table';
import EmptyState from '@atlaskit/empty-state';
import Modal, {
  ModalBody,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Flex } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { useCallback, useEffect, useState } from 'react';

import { initDatabricksApi } from '@/api/databricks';
import { CloudSite } from '@/components/common/types';
import { useAppContext } from '@/store';

import LockClosed from '../../assets/lock-closed.svg';

import { DatabricksConfigForm } from './config-form';

export const DatabricksOnboard = () => {
  const { state } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  const [cloudSiteId, setCloudSiteId] = useState('');
  const { subscriptionDetails, loading } = state;

  useEffect(() => {
    initDatabricksApi();
  }, []);

  if (loading) {
    return <Spinner size='large' />;
  }

  if (subscriptionDetails.length > 0) {
    return (
      <>
        <SubscriptionDetailsTable
          subscriptionDetails={subscriptionDetails}
          openModal={openModal}
          setCloudSiteId={setCloudSiteId}
        />
        <ModalTransition>
          {isOpen && (
            <Modal onClose={closeModal} shouldScrollInViewport width="x-large">
              <ModalHeader hasCloseButton>
                <ModalTitle>Databricks configuration</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <DatabricksConfigForm
                  closeModal={closeModal}
                  cloudSiteId={cloudSiteId}
                />
              </ModalBody>
            </Modal>
          )}
        </ModalTransition>
      </>
    );
  }

  return <EmptyStateView />;
};

const SubscriptionDetailsTable = ({
  subscriptionDetails,
  openModal,
  setCloudSiteId,
}: {
  subscriptionDetails: CloudSite[];
  openModal: () => void;
  setCloudSiteId: (cloudSiteId: string) => void;
}) => {
  const rows = subscriptionDetails.map(subscription => ({
    key: subscription.cloudSiteId,
    cells: [
      {
        key: 'hostname',
        content: subscription.hostname,
      },
      {
        key: 'action',
        content: (
          <Button
            appearance='subtle'
            onClick={() => {
              setCloudSiteId(subscription.cloudSiteId);
              openModal();
            }}
          >
            Manage
          </Button>
        ),
      },
    ],
  }));

  const head = {
    cells: [
      {
        key: 'hostname',
        content: 'Hostname',
      },
      {
        key: 'action',
        content: <span style={{ marginLeft: "12px" }}>Action</span>,
      },
    ],
  };

  return (
    <Flex direction='column' alignItems='center'>
      <Box style={{ width: '80%', marginTop: '20px' }}>
        <h2 style={{ marginBottom: '20px' }}>Cloud sites</h2>
        <DynamicTable
          head={head}
          rows={rows}
          rowsPerPage={50}
          defaultPage={1}
          emptyView={<AtlasEmptyState />}
          loadingSpinnerSize='large'
          isRankable={false}
        />
      </Box>
    </Flex>
  );
};

const EmptyStateView = () => {
  return (
    <Box style={{ marginTop: '10%' }}>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <LockClosed height={200} width={200} />
      </Box>
      <AtlasEmptyState />
    </Box>
  );
};

const AtlasEmptyState = () => {
  return (
    <EmptyState
      header="You don't have access to any cloud sites"
      description="To manage Databricks configuration, please make sure you have access to at least one cloud site in your organization."
      headingLevel={2}
    />
  );
};
